import { z } from 'zod';

/**
 * Retourenmanagement nach dem Vorbild Zalando (Briefing Teil D, briefing/DXL-Commerce-Briefing-Teil-D-Retouren.md).
 * Eine Retoure gehört zu genau einer Kundenbestellung; je Rücksendeziel ein Paket; je zurückgegebenem Stück eine
 * Retourenposition. Erstattet wird immer vom Marktplatz, der Händler wird über sein Buchungsjournal verrechnet.
 */

/* ------------------------------------------------------------------ Begriffe */

/** Gründe je Stück (Pflicht im Kundenportal) */
export const RETOUREN_GRUENDE = {
  zu_gross: 'Zu groß',
  zu_klein: 'Zu klein',
  gefaellt_nicht: 'Gefällt mir nicht',
  anders_als_abgebildet: 'Anders als abgebildet',
  qualitaet: 'Qualität entspricht nicht den Erwartungen',
  falscher_artikel_erhalten: 'Falschen Artikel erhalten',
  defekt: 'Artikel ist defekt',
  zu_spaet: 'Lieferung kam zu spät',
  doppelt_bestellt: 'Doppelt bestellt',
  sonstiges: 'Sonstiges',
} as const;
export type RetourenGrund = keyof typeof RETOUREN_GRUENDE;
export const RETOUREN_GRUND_CODES = Object.keys(RETOUREN_GRUENDE) as [RetourenGrund, ...RetourenGrund[]];
/** Bei diesen Gründen ist eine Kurzbeschreibung Pflicht; Fotos kann der Kunde hochladen (freiwillig – der Widerruf braucht keinen Nachweis) */
export const GRUENDE_MIT_FOTO: readonly RetourenGrund[] = ['defekt', 'falscher_artikel_erhalten'];

/** Status einer Retourenposition (ein Stück) */
export const POSITION_STATUS = {
  angemeldet: 'Angemeldet',
  unterwegs: 'Unterwegs',
  eingegangen: 'Eingegangen',
  angenommen: 'Angenommen',
  auto_angenommen: 'Automatisch angenommen',
  klaerfall: 'Klärfall',
  erstattet: 'Erstattet',
  abgelehnt: 'Abgelehnt',
  storniert: 'Storniert',
} as const;
export type RetourenPositionStatus = keyof typeof POSITION_STATUS;
export const POSITION_STATUS_CODES = Object.keys(POSITION_STATUS) as [RetourenPositionStatus, ...RetourenPositionStatus[]];

/** Status der Retoure – abgeleitet aus den Positionen */
export const RETOURE_STATUS = {
  angemeldet: 'Angemeldet',
  teilweise_unterwegs: 'Teilweise unterwegs',
  unterwegs: 'Unterwegs',
  teilweise_eingegangen: 'Teilweise eingegangen',
  in_pruefung: 'In Prüfung',
  teilweise_erstattet: 'Teilweise erstattet',
  erstattet: 'Erstattet',
  abgeschlossen: 'Abgeschlossen',
  storniert: 'Storniert',
} as const;
export type RetoureStatus = keyof typeof RETOURE_STATUS;
export const RETOURE_STATUS_CODES = Object.keys(RETOURE_STATUS) as [RetoureStatus, ...RetoureStatus[]];

export const RETOURE_QUELLEN = {
  portal: 'Retourenportal',
  shop_konto: 'Kundenkonto im Shop',
  support: 'Kundenservice',
  api: 'Schnittstelle',
  ohne_anmeldung: 'Ohne Anmeldung zurückgeschickt',
} as const;
export type RetoureQuelle = keyof typeof RETOURE_QUELLEN;

export const RETOUREN_PRUEFERGEBNISSE = {
  a_ware: 'A-Ware',
  b_ware: 'B-Ware',
  defekt: 'Defekt',
  falscher_artikel: 'Falscher Artikel',
  fehlt: 'Fehlt (leeres Paket)',
  gebraucht: 'Gebraucht/beschädigt durch Kunde',
} as const;
export type RetourenPruefergebnis = keyof typeof RETOUREN_PRUEFERGEBNISSE;

export const KLAERFALL_ARTEN = {
  leer: 'Leeres Paket',
  gebraucht: 'Getragen oder gebraucht',
  falscher_artikel: 'Falscher Artikel',
  faelschung: 'Fälschung',
  frist_ueberschritten: 'Frist überschritten',
  beschaedigt_kunde: 'Vom Kunden beschädigt',
  sonstiges: 'Sonstiges',
} as const;
export type KlaerfallArt = keyof typeof KLAERFALL_ARTEN;

export const KLAERFALL_ENTSCHEIDUNGEN = {
  voll_erstatten: 'Voll erstatten',
  teil_erstatten: 'Teilweise erstatten',
  ablehnen_zuruecksenden: 'Ablehnen, Ware an Kunden zurück',
  ablehnen_entsorgen: 'Ablehnen, Ware entsorgen (mit Einwilligung)',
} as const;
export type KlaerfallEntscheidung = keyof typeof KLAERFALL_ENTSCHEIDUNGEN;

export const RETOUREN_MODI = {
  direkt: 'Direkt an den Händler',
  zentral: 'Zentral über den Marktplatz',
} as const;
export type RetourenModus = keyof typeof RETOUREN_MODI;

/** Label-Quellen A–C (Abschnitt 7.2). D (Beilage-Label) ist zusätzlich schaltbar und kombiniert mit A oder B. */
export const LABEL_QUELLEN = {
  marktplatz: 'Vertrag des Marktplatzes',
  haendler: 'Eigener Vertrag beim Versanddienstleister',
  bereitstellen: 'Händler stellt das Label selbst bereit',
} as const;
export type LabelQuelle = keyof typeof LABEL_QUELLEN;

export const RETOUREN_CARRIER = {
  dhl: 'DHL',
  dpd: 'DPD',
  hermes: 'Hermes',
  gls: 'GLS',
  ups: 'UPS',
  post_at: 'Österreichische Post',
  post_ch: 'Schweizerische Post',
} as const;
export type RetourenCarrier = keyof typeof RETOUREN_CARRIER;

export const PAKET_ZIELE = { marktplatz: 'Marktplatz-Lager', zentrale: 'Retourenzentrum', haendler: 'Händler' } as const;
export type PaketZielTyp = keyof typeof PAKET_ZIELE;

export const LABEL_STATUS = { ausstehend: 'Wird erstellt', erstellt: 'Erstellt', fehler: 'Fehler', manuell_angefordert: 'Beim Händler angefordert', abholung: 'Abholung nötig (Sperrgut)' } as const;
export type LabelStatus = keyof typeof LABEL_STATUS;

export const CARRIER_STATUS = { angemeldet: 'Angemeldet', abgegeben: 'Abgegeben', unterwegs: 'Unterwegs', zugestellt: 'Zugestellt' } as const;
export type CarrierStatus = keyof typeof CARRIER_STATUS;

export const KOSTEN_TRAEGER = { marktplatz: 'Marktplatz', haendler: 'Händler', kunde: 'Kunde' } as const;
export type KostenTraeger = keyof typeof KOSTEN_TRAEGER;

export const ERSTATTUNG_ZEITPUNKTE = {
  bei_eingang: 'Nach Eingang und Prüfung',
  bei_uebergabe_carrier: 'Schon bei Übergabe an den Versanddienstleister',
} as const;
export type ErstattungZeitpunkt = keyof typeof ERSTATTUNG_ZEITPUNKTE;

export const VERSANDKOSTEN_ERSTATTEN = {
  bei_vollretoure: 'Bei vollständiger Rückgabe (gesetzlicher Standard)',
  immer: 'Immer',
  nie: 'Nie',
} as const;
export type VersandkostenErstatten = keyof typeof VERSANDKOSTEN_ERSTATTEN;

/** Wie viel Provision der Händler bei einer Retoure zurückbekommt (Abschnitt 10.4) */
export const PROVISION_RUECKGABE = {
  voll: 'Voll zurück',
  anteilig: 'Anteilig zurück',
  keine: 'Keine Rückgabe (Retourenprovision)',
} as const;
export type ProvisionRueckgabe = keyof typeof PROVISION_RUECKGABE;

/**
 * Ausschlüsse vom Widerrufsrecht – nur die gesetzlich zulässigen Fälle (§ 312g Abs. 2 BGB). Andere Ausschlüsse kann die
 * Oberfläche nicht speichern. Vorlage, rechtliche Prüfung vorbehalten.
 */
export const AUSSCHLUSS_GRUENDE = {
  personalisiert: 'Nach Kundenwunsch angefertigt oder personalisiert',
  verderblich: 'Schnell verderblich oder Verfallsdatum überschritten',
  hygiene: 'Versiegelt aus Gesundheits- oder Hygienegründen, Versiegelung entfernt',
  vermischt: 'Nach Lieferung untrennbar mit anderen Gütern vermischt',
  datentraeger: 'Ton-, Videoaufnahmen oder Software in versiegelter Packung, Versiegelung entfernt',
  zeitungen: 'Zeitungen, Zeitschriften und Illustrierte',
} as const;
export type AusschlussGrund = keyof typeof AUSSCHLUSS_GRUENDE;

/** Gesetzliche Widerrufsfrist in Tagen ab Erhalt – keine Einstellung darf darunter liegen */
export const WIDERRUF_MINDESTFRIST = 14;

/* ------------------------------------------------------------------ Eingaben */

const Cent = z.number({ invalid_type_error: 'Bitte einen Betrag angeben' }).int().min(0, 'Mindestens 0 €').max(100_000_00, 'Höchstens 100.000 €');
const Frist = z
  .number({ invalid_type_error: 'Bitte eine Zahl eingeben' })
  .int('Bitte ganze Tage angeben')
  .min(WIDERRUF_MINDESTFRIST, `Mindestens ${WIDERRUF_MINDESTFRIST} Tage – das ist die gesetzliche Widerrufsfrist`)
  .max(365, 'Höchstens 365 Tage');
const Text = (max: number) => z.string().trim().max(max);

export const AusschlussSchema = z.object({
  kategorie: z.string().trim().min(1, 'Bitte eine Kategorie angeben').max(120),
  grund: z.enum(Object.keys(AUSSCHLUSS_GRUENDE) as [AusschlussGrund, ...AusschlussGrund[]], {
    errorMap: () => ({ message: 'Nur gesetzlich zulässige Ausschlüsse' }),
  }),
});
export type Ausschluss = z.infer<typeof AusschlussSchema>;
const Ausschluesse = z
  .array(AusschlussSchema)
  .max(200, 'Höchstens 200 Ausschlüsse')
  .refine((l) => new Set(l.map((a) => a.kategorie.toLowerCase())).size === l.length, 'Jede Kategorie nur einmal');

/** Retouren-Richtlinie des Marktplatzes. Jede Änderung legt eine neue Version an; Retouren behalten ihre Version. */
export const RetourenRichtlinieSchema = z
  .object({
    fristTage: Frist,
    kostenlos: z.boolean(),
    /** Abzug je Retoure in Cent, nur wenn nicht kostenlos */
    kostenKunde: Cent,
    ausschluesse: Ausschluesse,
    haendlerAbweichungErlaubt: z.boolean(),
    /** Werktage nach Zustellung beim Ziel, danach gilt eine nicht gebuchte Retoure als angenommen */
    autoAnnahmeTage: z.number().int().min(1, 'Mindestens 1 Werktag').max(30, 'Höchstens 30 Werktage'),
    /** Werktage nach Zustellung, in denen Händler Auffälligkeiten melden können */
    klaerfallFristWerktage: z.number().int().min(1, 'Mindestens 1 Werktag').max(14, 'Höchstens 14 Werktage'),
    erstattungZeitpunkt: z.enum(Object.keys(ERSTATTUNG_ZEITPUNKTE) as [ErstattungZeitpunkt, ...ErstattungZeitpunkt[]]),
    versandkostenErstatten: z.enum(Object.keys(VERSANDKOSTEN_ERSTATTEN) as [VersandkostenErstatten, ...VersandkostenErstatten[]]),
    /** Standard-Versandkosten in Cent; mehr wird als Expresszuschlag nicht erstattet. null = gezahlte Versandkosten */
    versandStandard: Cent.nullable(),
    provisionRueckgabe: z.enum(Object.keys(PROVISION_RUECKGABE) as [ProvisionRueckgabe, ...ProvisionRueckgabe[]]),
    /** Bei „anteilig“: so viel Prozent der Provision bekommt der Händler zurück */
    provisionRueckgabeProzent: z.number().min(0, 'Mindestens 0 %').max(100, 'Höchstens 100 %').multipleOf(0.01, 'Höchstens zwei Nachkommastellen'),
    /** Kosten der Rücksendelabels über den Vertrag des Marktplatzes */
    labelKostenTraeger: z.enum(['marktplatz', 'haendler']),
    /** Gebühren in Cent laut Händlervertrag */
    gebuehrZentrumPosition: Cent,
    gebuehrFristversaeumnis: Cent,
    /** Label-Quelle „Händler stellt bereit“: so viele Arbeitsstunden hat der Händler, danach Erinnerung; nach 24 h Ersatz-Label */
    labelBereitstellenStunden: z.number().int().min(1, 'Mindestens 1 Stunde').max(24, 'Höchstens 24 Stunden'),
  })
  .superRefine((r, ctx) => {
    if (r.kostenlos && r.kostenKunde > 0) ctx.addIssue({ code: 'custom', path: ['kostenKunde'], message: 'Bei kostenloser Rücksendung gibt es keinen Abzug' });
    if (!r.kostenlos && r.kostenKunde === 0) ctx.addIssue({ code: 'custom', path: ['kostenKunde'], message: 'Bitte den Abzug je Retoure angeben' });
  });
export type RetourenRichtlinieEingabe = z.infer<typeof RetourenRichtlinieSchema>;

/** Vorgabe für neue Marktplätze */
export const RICHTLINIE_STANDARD: RetourenRichtlinieEingabe = {
  fristTage: 30,
  kostenlos: true,
  kostenKunde: 0,
  ausschluesse: [],
  haendlerAbweichungErlaubt: false,
  autoAnnahmeTage: 5,
  klaerfallFristWerktage: 2,
  erstattungZeitpunkt: 'bei_eingang',
  versandkostenErstatten: 'bei_vollretoure',
  versandStandard: null,
  provisionRueckgabe: 'voll',
  provisionRueckgabeProzent: 100,
  labelKostenTraeger: 'marktplatz',
  gebuehrZentrumPosition: 0,
  gebuehrFristversaeumnis: 0,
  labelBereitstellenStunden: 4,
};

export interface RetourenRichtlinie extends RetourenRichtlinieEingabe {
  id: string | null;
  /** 0 = noch nicht gespeichert, es gilt die Vorgabe */
  version: number;
  gueltigAb: string | null;
}

const Land = z.string().trim().toUpperCase().regex(/^[A-Z]{2}$/, 'Ländercode mit zwei Buchstaben, z. B. DE');

export const RetourenAdresseSchema = z.object({
  firma: Text(200).min(1, 'Bitte die Firma angeben'),
  strasse: Text(200).min(1, 'Bitte Straße und Hausnummer angeben'),
  plz: Text(20).min(1, 'Bitte die PLZ angeben'),
  ort: Text(120).min(1, 'Bitte den Ort angeben'),
  land: Land,
  kontakt: Text(200).default(''),
});
export type RetourenAdresse = z.infer<typeof RetourenAdresseSchema>;

/* ------------------------------------------------------------------ Versanddienstleister (Etappe D2) */

/**
 * Empfänger-IDs beim Versanddienstleister je Absenderland (DHL: „receiverId“ aus dem Geschäftskundenportal, z. B. „deu“).
 * Schlüssel: Ländercode oder „*“ für alle übrigen Länder. Die Rücksendeadresse steht bei DHL hinter der ID, nicht im Auftrag.
 */
export const EmpfaengerIdsSchema = z
  .record(z.string().regex(/^([A-Z]{2}|\*)$/, 'Ländercode mit zwei Buchstaben oder *'), z.string().trim().regex(/^[A-Za-z0-9_-]{2,40}$/, 'Ungültige Empfänger-ID'))
  .refine((r) => Object.keys(r).length <= 40, 'Höchstens 40 Länder');
export type EmpfaengerIds = z.infer<typeof EmpfaengerIdsSchema>;

/** Empfänger-ID für ein Absenderland: erst das Land, sonst „*“ */
export function empfaengerIdFuer(ids: EmpfaengerIds | null | undefined, land: string): string | null {
  if (!ids) return null;
  return ids[land.trim().toUpperCase()] ?? ids['*'] ?? null;
}

/** „DE: deu, AT: aut, *: deu“ ↔ { DE: 'deu', AT: 'aut', '*': 'deu' } – für ein einfaches Eingabefeld. Eine ID ohne Land gilt für alle (*). */
export function empfaengerIdsAusText(text: string): EmpfaengerIds {
  const aus: EmpfaengerIds = {};
  for (const teil of text.split(/[,;\n]/).map((t) => t.trim()).filter(Boolean)) {
    const m = /^([A-Za-z]{2}|\*)\s*[:=]\s*(.+)$/.exec(teil);
    if (m) aus[m[1]!.toUpperCase()] = m[2]!.trim();
    else aus['*'] = teil;
  }
  return aus;
}
export function empfaengerIdsText(ids: EmpfaengerIds | null | undefined): string {
  return Object.entries(ids ?? {}).sort(([a], [b]) => (a === '*' ? 1 : b === '*' ? -1 : a.localeCompare(b))).map(([l, id]) => `${l}: ${id}`).join(', ');
}

/** Zugang zum Geschäftskundenportal des Versanddienstleisters. Passwort leer = bisheriges behalten. */
export const CarrierZugangSchema = z.object({
  benutzer: z.string().trim().min(1, 'Bitte den Benutzer angeben').max(120),
  passwort: z.string().max(200).default(''),
  /** Optional eigener Benutzer für die Sendungsverfolgung (Recht „Sendungen verfolgen“), sonst gilt der obige */
  trackingBenutzer: z.string().trim().max(120).default(''),
  trackingPasswort: z.string().max(200).default(''),
});
export type CarrierZugangEingabe = z.input<typeof CarrierZugangSchema>;

/** Zugang ohne Geheimnisse für die Oberfläche */
export interface CarrierZugangAnzeige {
  carrier: RetourenCarrier;
  benutzer: string;
  trackingBenutzer: string;
  geprueftAm: string | null;
  /** leer = letzte Prüfung erfolgreich */
  pruefFehler: string;
}

export const VerbindungTestSchema = z.object({ empfaengerId: z.string().trim().regex(/^[A-Za-z0-9_-]{2,40}$/, 'Ungültige Empfänger-ID').optional() });

export interface VerbindungTestErgebnis {
  ok: boolean;
  text: string;
  sendungsnummer?: string;
}

/** Wie DXL mit dem Versanddienstleister spricht: echte Schnittstelle (Sandbox/Produktion) oder Attrappe (Demo, Tests) */
export const CARRIER_UMGEBUNGEN = { produktion: 'Produktion', sandbox: 'Sandbox (Testumgebung)', attrappe: 'Attrappe (Demo)', fehlt: 'Nicht eingerichtet' } as const;
export type CarrierUmgebung = keyof typeof CARRIER_UMGEBUNGEN;

/** Beilage-Label (Quelle D): beim Versand erzeugt, liegt dem Paket bei und wird bei der Anmeldung wiederverwendet */
export interface BeilageLabel {
  id: string;
  teilId: string;
  carrier: RetourenCarrier;
  sendungsnummer: string;
  gueltigBis: string | null;
  labelUrl: string;
  /** null = kein QR-Code (Absender außerhalb Deutschlands) */
  qrUrl: string | null;
  verwendet: boolean;
}

/** Rücksendeziel des Marktplatzes: eigenes Lager oder Retourenzentrum, zuständig für Länder (leer = alle übrigen) */
export const RetourenZielSchema = z.object({
  art: z.enum(['lager', 'zentrum']),
  name: Text(120).min(1, 'Bitte einen Namen angeben'),
  adresse: RetourenAdresseSchema,
  laender: z.array(Land).max(60).default([]),
  /** Nur beim Lager: dient zugleich als Retourenzentrum – eigene Ware und zentral abgewickelte Händler in einem Paket */
  auchZentrum: z.boolean().default(false),
  aktiv: z.boolean().default(true),
  /** Empfänger-IDs je Versanddienstleister im Konto des Marktplatzes, z. B. { dhl: { DE: 'deu' } } */
  empfaengerIds: z.record(z.enum(Object.keys(RETOUREN_CARRIER) as [RetourenCarrier, ...RetourenCarrier[]]), EmpfaengerIdsSchema).default({}),
});
export type RetourenZielEingabe = z.input<typeof RetourenZielSchema>;
export interface RetourenZiel extends z.infer<typeof RetourenZielSchema> {
  id: string;
}

/** Einstellung, die der Marktplatz je Händler festlegt (Modus kostet laut Händlervertrag) */
export const HaendlerRetourenModusSchema = z.object({
  modus: z.enum(Object.keys(RETOUREN_MODI) as [RetourenModus, ...RetourenModus[]]),
  /** Empfänger-IDs dieses Händlers im DHL-Konto des Marktplatzes (Label-Quelle „marktplatz“) – richtet der DHL-Vertrieb ein */
  marktplatzEmpfaenger: EmpfaengerIdsSchema.optional(),
});

/** Einstellung, die der Händler im Portal pflegt */
export const HaendlerRetourenEinstellungSchema = z.object({
  labelQuelle: z.enum(Object.keys(LABEL_QUELLEN) as [LabelQuelle, ...LabelQuelle[]]),
  beilageLabel: z.boolean(),
  carrier: z.enum(Object.keys(RETOUREN_CARRIER) as [RetourenCarrier, ...RetourenCarrier[]]),
  /** null = Frist des Marktplatzes. Nur wenn der Marktplatz Abweichungen erlaubt. */
  fristTage: Frist.nullable(),
  /** null = Ausschlüsse des Marktplatzes. Nur wenn der Marktplatz Abweichungen erlaubt. */
  ausschluesse: Ausschluesse.nullable(),
  /** Empfänger-IDs im eigenen DHL-Konto (Label-Quelle „haendler“) */
  eigeneEmpfaenger: EmpfaengerIdsSchema.default({}),
});
export type HaendlerRetourenEinstellungEingabe = z.infer<typeof HaendlerRetourenEinstellungSchema>;

export interface HaendlerRetourenEinstellung extends HaendlerRetourenEinstellungEingabe {
  modus: RetourenModus;
  /** Rücksendeadresse aus den Stammdaten des Händlers (keine zweite Quelle). null = noch nicht vollständig */
  adresse: RetourenAdresse | null;
  /** Darf der Händler Frist und Ausschlüsse abweichend setzen? */
  abweichungErlaubt: boolean;
  /** Frist und Ausschlüsse des Marktplatzes zum Vergleich */
  richtlinie: Pick<RetourenRichtlinieEingabe, 'fristTage' | 'ausschluesse'>;
  /** Eigener Zugang zum Versanddienstleister hinterlegt (Label-Quelle „haendler“) */
  zugangHinterlegt: boolean;
  /** Eigener Zugang ohne Geheimnisse (Label-Quelle „haendler“) */
  zugang: CarrierZugangAnzeige | null;
  /** Empfänger-IDs des Händlers im Konto des Marktplatzes (Label-Quelle „marktplatz“); legt der Marktplatz fest */
  marktplatzEmpfaenger: EmpfaengerIds;
}

export const HAENDLER_EINSTELLUNG_STANDARD: HaendlerRetourenEinstellungEingabe & { modus: RetourenModus } = {
  modus: 'direkt',
  labelQuelle: 'marktplatz',
  beilageLabel: false,
  carrier: 'dhl',
  fristTage: null,
  ausschluesse: null,
  eigeneEmpfaenger: {},
};

/** Antwort von GET /api/konto/retouren/einstellungen */
export interface RetourenEinstellungen {
  richtlinie: RetourenRichtlinie;
  ziele: RetourenZiel[];
  /** Kategorien des Marktplatzes zur Auswahl der Ausschlüsse */
  kategorien: string[];
  /** DHL-Zugang des Marktplatzes (Label-Quelle „marktplatz“, Lager und Retourenzentren) */
  zugang: CarrierZugangAnzeige | null;
  /** Verbindung von DXL zu DHL: Produktion, Sandbox, Attrappe oder nicht eingerichtet (Fly-Secrets DHL_API_KEY/DHL_API_SECRET) */
  umgebung: CarrierUmgebung;
}

/* ------------------------------------------------------------------ Rücksendeadresse aus den Stammdaten */

const LAENDER: Record<string, string> = {
  deutschland: 'DE', germany: 'DE', österreich: 'AT', oesterreich: 'AT', austria: 'AT', schweiz: 'CH', switzerland: 'CH',
  niederlande: 'NL', netherlands: 'NL', belgien: 'BE', belgium: 'BE', luxemburg: 'LU', luxembourg: 'LU', frankreich: 'FR', france: 'FR',
  italien: 'IT', italy: 'IT', spanien: 'ES', spain: 'ES', portugal: 'PT', polen: 'PL', poland: 'PL', tschechien: 'CZ', czechia: 'CZ',
  dänemark: 'DK', daenemark: 'DK', denmark: 'DK', schweden: 'SE', sweden: 'SE', finnland: 'FI', finland: 'FI', irland: 'IE', ireland: 'IE',
  liechtenstein: 'LI', ungarn: 'HU', hungary: 'HU', slowakei: 'SK', slowenien: 'SI', kroatien: 'HR', griechenland: 'GR',
  'vereinigtes königreich': 'GB', 'united kingdom': 'GB', großbritannien: 'GB',
};

/** Ländername aus den Stammdaten („Deutschland“) oder Code („DE“) → ISO-Code; leer = unbekannt */
export function landCode(land: string): string {
  const t = land.trim();
  if (/^[A-Za-z]{2}$/.test(t)) return t.toUpperCase();
  return LAENDER[t.toLowerCase()] ?? '';
}

export interface HaendlerStammAdresse {
  firma: string;
  ansprechpartner?: string;
  strasse: string;
  /** „PLZ Ort“ wie in den Stammdaten */
  ort: string;
  land: string;
  ruecksendungAbweichend: boolean;
  ruecksendungName: string;
  ruecksendungStrasse: string;
  ruecksendungOrt: string;
  ruecksendungLand: string;
}

/**
 * Rücksendeadresse eines Händlers aus seinen Stammdaten: abweichende Rücksendeadresse, sonst Geschäftsanschrift.
 * null, wenn etwas fehlt (Straße, PLZ, Ort oder ein bekanntes Land).
 */
export function haendlerRuecksendeadresse(h: HaendlerStammAdresse): RetourenAdresse | null {
  const ab = h.ruecksendungAbweichend;
  const strasse = (ab ? h.ruecksendungStrasse : h.strasse).trim();
  const ortZeile = (ab ? h.ruecksendungOrt : h.ort).trim();
  const land = landCode((ab ? h.ruecksendungLand || h.land : h.land) || 'Deutschland');
  const m = /^([A-Za-z]{0,3}-?\d[\d\s-]{2,9})\s+(.+)$/.exec(ortZeile);
  if (!strasse || !m || !land) return null;
  return {
    firma: (ab && h.ruecksendungName.trim()) || h.firma.trim(),
    strasse,
    plz: m[1]!.trim(),
    ort: m[2]!.trim(),
    land,
    kontakt: ab ? '' : (h.ansprechpartner ?? '').trim(),
  };
}
